import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/takeUntil';
import { AssetPerson } from '../../models/employee-asset.model';
import { OffboardingAssignment, OffboardingAssignmentFunction, OffboardingPermissions } from '../../models/offboarding.model';
import { OffboardingService } from '../../services/offboarding.service';
import { assetErrorKey, workflowTrackBy } from '../common/employee-assets/asset.helpers';

const SETTINGS = {empty: '', zero: 0, denied: 'ASSET_DENIED', sysadmin: 'SERVICE_OWNER'};
interface AssignmentRow {key: string; titleKey: string; assignment: OffboardingAssignment;}
@Component({selector: 'app-offboarding-assignments', templateUrl: './offboarding-assignments.component.html',
  styleUrls: ['./offboarding-assignments.component.css', './offboarding-sequential.component.css']})
export class OffboardingAssignmentsComponent implements OnInit, OnDestroy {
  @Input() embedded = false;
  trackBy = workflowTrackBy;
  items: OffboardingAssignment[] = [];
  functions: OffboardingAssignmentFunction[] = [];
  rows: AssignmentRow[] = [];
  locations: AssetPerson[] = [];
  permissions: OffboardingPermissions;
  selectedLocation = SETTINGS.empty;
  pendingLocation = SETTINGS.empty;
  editing: AssignmentRow;
  loading = false;
  editorBusy = false;
  errorKey = SETTINGS.empty;
  saved = false;
  private destroyed = new Subject<void>();
  private permissionRequest: Subscription;
  private optionsRequest: Subscription;
  private assignmentsRequest: Subscription;
  constructor(private api: OffboardingService, private changeDetectorRef: ChangeDetectorRef) {}
  ngOnInit(): void { this.loadPermissions(); }
  ngOnDestroy(): void { this.destroyed.next(); this.destroyed.complete(); }
  loadPermissions(): void {
    this.stopReads();
    this.permissions = null; this.errorKey = SETTINGS.empty;
    this.permissionRequest = this.api.permissions().takeUntil(this.destroyed).subscribe(result => {
      this.permissions = result;
      if (result.canConfigure) { this.loadOptions(); }
      this.changeDetectorRef.markForCheck();
    }, error => { this.errorKey = assetErrorKey(error, false); this.clear(); this.changeDetectorRef.markForCheck(); });
  }
  loadOptions(): void {
    if (this.optionsRequest) { this.optionsRequest.unsubscribe(); }
    this.optionsRequest = this.api.options(SETTINGS.empty).takeUntil(this.destroyed).subscribe(result => {
      if (!this.permissions || !this.permissions.canConfigure) { return; }
      this.locations = result.locations || [];
      this.functions = (result.assignmentFunctions || []).filter(item => item.key === SETTINGS.sysadmin);
      if (!this.selectedLocation && this.locations.length) { this.selectedLocation = this.locations[SETTINGS.zero].id; }
      if (this.selectedLocation) { this.load(); } else { this.projectRows(); }
      this.changeDetectorRef.markForCheck();
    }, error => this.fail(error));
  }
  load(): void {
    if (!this.selectedLocation) { return; }
    if (this.assignmentsRequest) { this.assignmentsRequest.unsubscribe(); }
    this.loading = true; this.errorKey = SETTINGS.empty;
    const location = this.selectedLocation;
    this.assignmentsRequest = this.api.assignments(location).takeUntil(this.destroyed).subscribe(result => {
      if (!this.permissions || !this.permissions.canConfigure) { return; }
      if (this.selectedLocation !== location) { return; }
      this.items = result; this.loading = false; this.projectRows();
      this.changeDetectorRef.markForCheck();
    }, error => this.fail(error));
  }
  selectLocation(code: string): void {
    if (this.editorBusy || code === this.selectedLocation) { return; }
    if (this.editing) { this.pendingLocation = code; return; }
    this.selectedLocation = code; this.saved = false; this.items = []; this.projectRows(); this.load();
  }
  discardLocationChange(): void {
    if (this.editorBusy) { return; }
    this.editing = null; this.selectedLocation = this.pendingLocation;
    this.pendingLocation = SETTINGS.empty; this.items = []; this.projectRows(); this.load();
  }
  keepEditing(): void { this.pendingLocation = SETTINGS.empty; }
  edit(row: AssignmentRow): void { if (!this.editing && !row.assignment.waitReason) { this.editing = row; this.saved = false; } }
  cancel(): void { if (!this.editorBusy) { this.editing = null; this.pendingLocation = SETTINGS.empty; } }
  savedPair(): void { this.cancel(); this.saved = true; this.load(); }
  retry(): void { if (!this.editorBusy && !this.editing) { this.loadPermissions(); } }
  private projectRows(): void {
    const editingKey = this.editing ? this.editing.key : SETTINGS.empty;
    this.rows = this.functions.map(descriptor => {
      const item = this.items.filter(value => value.function === descriptor.key
        && value.locationCode === this.selectedLocation && !value.departmentId)[SETTINGS.zero];
      return {key: descriptor.key, titleKey: descriptor.titleKey,
        assignment: item || {function: descriptor.key, assigneeId: SETTINGS.empty, locationCode: this.selectedLocation}};
    });
    this.editing = this.rows.filter(row => row.key === editingKey)[SETTINGS.zero];
  }
  private fail(error: {status?: number}): void {
    this.loading = false; this.errorKey = assetErrorKey(error, false);
    if (this.errorKey === SETTINGS.denied) { this.stopReads(); this.permissions = null; this.functions = []; this.locations = []; this.clear(); }
    this.changeDetectorRef.markForCheck();
  }
  private clear(): void { this.items = []; this.rows = []; this.editing = null; this.editorBusy = false; }
  private stopReads(): void {
    [this.permissionRequest, this.optionsRequest, this.assignmentsRequest].forEach(request => { if (request) { request.unsubscribe(); } });
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/offboarding/offboarding-assignments.component.ts